import React, { useState, useEffect } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate } from "react-router-dom";
import "./TrendingArtist.css";
import { toast } from "react-toastify";
import { Spinner, Modal, Button } from "react-bootstrap";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import SetTrendArtistPriorityModel from "../../../components/Model/SetTrendArtistPriorityModel";
import { artistImageUrl, extractObjectId } from "../../../utils/utils";
import PageTargetSelector from "../../../components/common/PageTargetSelector";
import usePageTargetSelection from "../../../hooks/usePageTargetSelection";
import {
  FaSearch,
  FaFire,
  FaStar,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaMusic,
  FaSortAmountDown
} from "react-icons/fa";

const TrendingArtist = () => {
  const navigate = useNavigate();
  const pageTarget = usePageTargetSelection();
  const targetParams = pageTarget.selectedTargetParams;
  const selectedTargetLabel = pageTarget.selectedTargetLabel;

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [trendingArtists, setTrendingArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(20);
  const [debounceQuery, setDebounceQuery] = useState(searchQuery);
  const [showTrendArtistPrioritEditModal, setTrendArtistPrioritShowEditModal] = useState(false);
  const [trendArtistEditData, setTrendArtistDetailEditData] = useState({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [artistToDelete, setArtistToDelete] = useState(null);

  const fetchTrendingArtists = async (page, limit) => {
    setLoading(true);
    try {
      const params = { limit, page, ...targetParams };

      let response = await allAPiServicesCall.getTrendingArtists(
        params,
        getAuthConfig()
      );

      const { totalPages, trendingList } = response.data;

      if (trendingList) {
        setTrendingArtists(trendingList);
        setTotalPages(totalPages);
      } else {
        console.warn("No data found or data is not an array");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to fetch trending artists");
    }
  };

  const fetchSearchResults = async (query) => {
    setLoading(true);
    try {
      let response = await allAPiServicesCall.getSearchArtist(
        { query },
        getAuthConfig()
      );
      const searchList = response.data || [];
      if (searchList.length > 0) {
        setSearchResults(searchList);
      } else {
        setSearchResults([]);
        toast.info("No results found");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to fetch search artists");
    }
  };

  const addArtistToTrends = async (artistId) => {
    setLoading(true);
    try {
      const payload = { artist_id: artistId, ...targetParams };
      const res = await allAPiServicesCall.addTrendingArtist(
        payload,
        getAuthConfig()
      );
      if (res?.success) {
        fetchTrendingArtists(currentPage, limit);
        toast.success(`Artist added to trending for ${selectedTargetLabel}`);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to add trending artist.");
    }
  };

  const addRecommendedArtist = async (artistId) => {
    setLoading(true);
    try {
      const payload = { artist_id: artistId, ...targetParams };
      await allAPiServicesCall.addRecommendedArtist(
        payload,
        getAuthConfig()
      );
      toast.success(`Artist added to recommended for ${selectedTargetLabel}`);
      navigate(
        payload.pageType === "genre" && payload.genreSlug
          ? `/recommended-artist?pageType=genre&genreSlug=${encodeURIComponent(payload.genreSlug)}`
          : "/recommended-artist?pageType=homepage"
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to add recommended artist.");
    }
  };

  const handleOnDelete = async () => {
    if (!artistToDelete) return;
    setLoading(true);
    try {
      let payload = { id: artistToDelete };
      const res = await allAPiServicesCall.deleteTrendingArtist(payload, getAuthConfig());
      if (res?.success) {
        fetchTrendingArtists(currentPage, limit);
        toast.success("Artist deleted successfully");
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error("There was an error deleting the artist!", error);
      toast.error("Failed to delete artist.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounceQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (debounceQuery.trim()) {
      fetchSearchResults(debounceQuery);
    } else {
      fetchTrendingArtists(currentPage, limit);
    }
  }, [debounceQuery, currentPage, limit, targetParams.pageType, targetParams.genreSlug]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
  };

  const confirmDelete = (artistId) => {
    setArtistToDelete(artistId);
    setShowDeleteModal(true);
  };

  const setTrendArtistPriority = (ArtistId) => {
    setTrendArtistPrioritShowEditModal(true);
    setTrendArtistDetailEditData(ArtistId);
  };

  const isSearchMode = searchQuery.trim().length > 0;

  return (
    <CommonLayout>
      <div className="trending-artists-page">
        {/* Header Section */}
        <div className="trending-header">
          <div className="trending-header-content">
            <div className="trending-header-icon">
              <FaFire />
            </div>
            <div className="trending-header-text">
              <h1 className="trending-title">
                Trending Artists
                {pageTarget.selectedTarget?.pageType === "genre" && (
                  <span className="trending-genre-tag">
                    {pageTarget.selectedTarget?.genreSlug}
                  </span>
                )}
              </h1>
              <p className="trending-subtitle">
                Manage and curate your trending artists collection
              </p>
            </div>
          </div>
          <div className="trending-stats">
            <div className="trending-stat-card">
              <FaMusic className="trending-stat-icon" />
              <div className="trending-stat-info">
                <span className="trending-stat-value">{trendingArtists.length}</span>
                <span className="trending-stat-label">Artists</span>
              </div>
            </div>
            <div className="trending-stat-card">
              <FaSortAmountDown className="trending-stat-icon" />
              <div className="trending-stat-info">
                <span className="trending-stat-value">{totalPages}</span>
                <span className="trending-stat-label">Pages</span>
              </div>
            </div>
          </div>
        </div>

        <PageTargetSelector
          pageTarget={pageTarget}
          subtitle="Switch between Homepage and Genre targets before curating trending artists."
        />

        {/* Search Section */}
        <div className="trending-search-section">
          <div className="trending-search-wrapper">
            <FaSearch className="trending-search-icon" />
            <input
              type="text"
              className="trending-search-input"
              placeholder="Search artists to add to trending or recommended..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button className="trending-search-clear" onClick={clearSearch}>
                <FaTimes />
              </button>
            )}
          </div>
          {isSearchMode && (
            <div className="trending-search-badge">
              <FaSearch />
              <span>Showing search results for "{searchQuery}"</span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="trending-content">
          {loading ? (
            <div className="trending-loading">
              <div className="trending-loading-spinner">
                <Spinner animation="border" />
              </div>
              <p className="trending-loading-text">Loading artists...</p>
            </div>
          ) : isSearchMode ? (
            // Search Results
            searchResults.length > 0 ? (
              <div className="trending-grid">
                {searchResults.map((result) => (
                  <div key={result.artist_id} className="trending-card trending-card-search">
                    <div className="trending-card-image-wrapper">
                      <img
                        src={artistImageUrl(result)}
                        alt={result?.artist_name || "Artist"}
                        className="trending-card-image"
                      />
                      <div className="trending-card-overlay">
                        <span className="trending-card-badge trending-card-badge-search">
                          <FaSearch /> Search Result
                        </span>
                      </div>
                    </div>
                    <div className="trending-card-content">
                      <h3 className="trending-card-name">{result?.artist_name}</h3>
                      <div className="trending-card-actions">
                        <button
                          className="trending-action-btn trending-action-btn-fire"
                          onClick={() => addArtistToTrends(result.artist_id)}
                        >
                          <FaFire /> Trending
                        </button>
                        <button
                          className="trending-action-btn trending-action-btn-star"
                          onClick={() => addRecommendedArtist(result.artist_id)}
                        >
                          <FaStar /> Recommended
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="trending-empty">
                <div className="trending-empty-icon">
                  <FaSearch />
                </div>
                <h3 className="trending-empty-title">No Results Found</h3>
                <p className="trending-empty-text">
                  We couldn't find any artists matching "{searchQuery}"
                </p>
                <button className="trending-empty-btn" onClick={clearSearch}>
                  Clear Search
                </button>
              </div>
            )
          ) : (
            // Trending Artists List
            trendingArtists.length > 0 ? (
              <div className="trending-grid">
                {trendingArtists.map((artist, index) => (
                  <div key={artist.id} className="trending-card">
                    <div className="trending-card-image-wrapper">
                      <img
                        src={artistImageUrl(artist)}
                        alt={artist?.artist_name || "Artist"}
                        className="trending-card-image"
                        onClick={() => navigate(`/artist/albums-songs/${artist.artist_id}`)}
                      />
                      <div className="trending-card-overlay">
                        <span className="trending-card-rank">#{(currentPage - 1) * limit + index + 1}</span>
                        <span className="trending-card-badge">
                          <FaFire /> Trending
                        </span>
                      </div>
                    </div>
                    <div className="trending-card-content">
                      <h3 className="trending-card-name">{artist?.artist_name}</h3>
                      <div className="trending-card-actions">
                        <button
                          className="trending-action-btn trending-action-btn-edit"
                          onClick={() => navigate(`/edit-artist/${artist.artist_id}`)}
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          className="trending-action-btn trending-action-btn-priority"
                          onClick={() => setTrendArtistPriority(artist)}
                        >
                          <FaSortAmountDown /> Priority
                        </button>
                        <button
                          className="trending-action-btn trending-action-btn-delete"
                          onClick={() => confirmDelete(extractObjectId(artist?._id))}
                        >
                          <FaTrash /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="trending-empty">
                <div className="trending-empty-icon">
                  <FaFire />
                </div>
                <h3 className="trending-empty-title">No Trending Artists</h3>
                <p className="trending-empty-text">
                  Search for artists above to add them to your trending list
                </p>
              </div>
            )
          )}
        </div>

        {/* Pagination */}
        {!isSearchMode && trendingArtists.length > 0 && totalPages > 1 && (
          <div className="trending-pagination">
            <button
              className="trending-pagination-btn"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              <FaChevronLeft /> Previous
            </button>
            <div className="trending-pagination-info">
              <span className="trending-pagination-current">{currentPage}</span>
              <span className="trending-pagination-separator">of</span>
              <span className="trending-pagination-total">{totalPages}</span>
            </div>
            <button
              className="trending-pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        className="trending-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTrash className="me-2" style={{ color: 'var(--danger)' }} />
            Confirm Removal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to remove this artist from trending?</p>
          <p className="text-muted small">This action can be undone by adding the artist again.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleOnDelete}>
            <FaTrash className="me-1" /> Remove
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Priority Modal */}
      <SetTrendArtistPriorityModel
        show={showTrendArtistPrioritEditModal}
        handleClose={() => setTrendArtistPrioritShowEditModal(false)}
        artistDetails={trendArtistEditData}
        handleFormSubmit={() => fetchTrendingArtists(currentPage, limit)}
      />
    </CommonLayout>
  );
};

export default TrendingArtist;
